import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { supabase } from "../../lib/supabase";
import { ROUTES } from "../../lib/routes";
import { analytics } from "../../lib/analytics";
import { AccountForm } from "./forms/AccountForm";
import { CompanyForm } from "./forms/CompanyForm";
import { ScopeForm } from "./forms/ScopeForm";
import { OTPVerification } from "../shared/OTPVerification";
interface Scope {
  code: string;
  name: string;
}

interface OnboardingStep {
  title: string;
  description: string;
  image: string;
}

const steps: OnboardingStep[] = [
  {
    title: "Create Your Account",
    description:
      "Set up your personal account to get started with our platform.",
    image:
      "https://images.unsplash.com/photo-1553877522-43269d4ea984?auto=format&fit=crop&q=80&w=2070",
  },
  {
    title: "Company Details",
    description:
      "Tell us about your company so we can better serve your needs.",
    image:
      "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80&w=2069",
  },
  {
    title: "Integration Scope",
    description: "Select the integration features you need for your solution.",
    image:
      "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=2070",
  },
];

export function SaasOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(0);
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [pendingUser, setPendingUser] = useState<any>(null);
  const [invitationToken, setInvitationToken] = useState<string | null>(null);
  const [invitationValid, setInvitationValid] = useState<boolean | null>(null);
  const [invitationId, setInvitationId] = useState<string | null>(null);
  const [createdUserId, setCreatedUserId] = useState<string | null>(null);

  // Form state
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [companyDetails, setCompanyDetails] = useState({
    company_name: "",
    website: "",
    contact_phone: "",
    address: "",
    postal_code: "",
    city: "",
    country: "",
    registration_number: "",
  });
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);

  useEffect(() => {
    if (currentStep === 2) {
      loadScopes();
    }
  }, [currentStep]);

  // Validate invitation token on mount
  useEffect(() => {
    const validateInvitation = async () => {
      const token = searchParams.get("token");
      
      if (!token) {
        setInvitationValid(false);
        setError("SaaS sign-up is invitation-only. Please contact us for an invitation link.");
        return;
      }

      setInvitationToken(token);
      setLoading(true);

      try {
        // Check if token exists and is valid
        const { data, error: tokenError } = await supabase
          .from("saas_invitations")
          .select("id, expires_at, used_at")
          .eq("token", token)
          .maybeSingle();

        if (tokenError) {
          console.error("Error validating invitation:", tokenError);
          // Check if it's an RLS policy error
          if (tokenError.message.includes("row-level security") || tokenError.message.includes("permission denied")) {
            setInvitationValid(false);
            setError("Unable to validate invitation. Please ensure you're not in private/incognito mode or contact support.");
          } else {
            setInvitationValid(false);
            setError("Invalid invitation link. Please contact us for a new invitation.");
          }
          return;
        }

        if (!data) {
          setInvitationValid(false);
          setError("Invalid invitation link. Please contact us for a new invitation.");
          return;
        }

        // Check if expired
        const expiresAt = new Date(data.expires_at);
        if (expiresAt < new Date()) {
          setInvitationValid(false);
          setError("This invitation link has expired. Please contact us for a new invitation.");
          return;
        }

        // Check if already used
        if (data.used_at) {
          setInvitationValid(false);
          setError("This invitation link has already been used. Please contact us for a new invitation.");
          return;
        }

        // Token is valid
        setInvitationId(data.id);
        setInvitationValid(true);
        setError("");
      } catch (err: any) {
        console.error("Error validating invitation:", err);
        setInvitationValid(false);
        setError("An error occurred while validating your invitation. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    validateInvitation();
  }, [searchParams]);

  const loadScopes = async () => {
    try {
      setLoading(true);
      setError("");
      
      // Define the restricted list of scopes by name
      const restrictedScopes = [
        "Reservations",
        "Guests", 
        "Hotel Configuration",
        "Products and Services",
        "Rates",
        "Rooms",
        "Webhooks"
      ];
      
      const { data, error: scopesError } = await supabase
        .from("pms_scopes")
        .select("id, name")
        .in("name", restrictedScopes)
        .order("name");

      if (scopesError) {
        console.error("Error loading scopes:", scopesError);
        // Check if it's an RLS policy error
        if (scopesError.message.includes("row-level security") || scopesError.message.includes("permission denied")) {
          setError("Unable to load integration scopes. Please ensure you're not in private/incognito mode or contact support.");
        } else {
          setError(`Failed to load integration scopes: ${scopesError.message}`);
        }
        return;
      }
      
      // Transform the data to match the expected format with code field
      const transformedData = (data || []).map(scope => ({
        code: scope.name.toLowerCase().replace(/\s+/g, '_'),
        name: scope.name
      }));
      
      if (transformedData.length === 0) {
        setError("No integration scopes found. Please contact support.");
        return;
      }
      
      setScopes(transformedData);
    } catch (err: any) {
      console.error("Error loading scopes:", err);
      setError(err.message || "Failed to load integration scopes");
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep === 0) {
      navigate("/signup");
    } else if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleUserCreation = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsExistingUser(false);
    setLoading(true);

    try {
      // Check if invitation is required and valid
      if (invitationValid === false) {
        setError("A valid invitation is required to sign up. Please contact us for an invitation link.");
        return;
      }

      if (invitationValid === null) {
        setError("Validating invitation... Please wait.");
        return;
      }

      // Validate required fields
      if (!firstName.trim() || !lastName.trim()) {
        throw new Error("First name and last name are required");
      }

      // Check if email is already registered
      const { data: existingProfile } = await supabase
        .from("profiles")
        .select("id")
        .eq("email", email)
        .maybeSingle();

      if (existingProfile) {
        setIsExistingUser(true);
        setError("An account with this email already exists.");
        return;
      }

      // If invitation token is valid, use direct sign-up (skip OTP)
      if (invitationToken && invitationValid) {
        // Create user account with email and password directly
        const { data: authData, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/extranet/saas`,
            data: {
              first_name: firstName,
              last_name: lastName,
              user_type: "saas",
            },
          },
        });

        if (signUpError) {
          if (signUpError.message.includes("already registered") || signUpError.message.includes("already been registered")) {
            setIsExistingUser(true);
            setError("An account with this email already exists.");
            return;
          }
          throw signUpError;
        }

        if (!authData.user) {
          throw new Error("Failed to create user account");
        }

        // Store the user ID for use in subsequent steps
        setCreatedUserId(authData.user.id);

        // Create profile - try multiple approaches like in SaasHotelOnboardingWidget
        let profileCreated = false;
        const profileCreationErrors: string[] = [];

        // Approach 1: If session exists, try direct insert
        if (authData.session) {
          console.log("Attempting Approach 1: Direct insert with session");
          const profileData = {
            id: authData.user.id,
            email,
            user_type: "saas",
            full_name: `${firstName} ${lastName}`,
          };
          
          const { error: directError } = await supabase.from("profiles").insert([profileData]);
          if (!directError) {
            console.log("Approach 1 succeeded: Profile created via direct insert");
            profileCreated = true;
          } else {
            console.error("Approach 1 failed:", directError);
            profileCreationErrors.push(`Direct insert: ${directError.message}`);
          }
        } else {
          console.log("Approach 1 skipped: No session available (email confirmation may be required)");
        }

        // Approach 2: Try RPC function (works even without session)
        if (!profileCreated) {
          console.log("Attempting Approach 2: RPC function create_profile_for_widget");
          const { error: rpcError } = await supabase.rpc('create_profile_for_widget', {
            user_id: authData.user.id,
            user_email: email,
            user_type: 'saas',
            full_name: `${firstName} ${lastName}`,
          });
          
          if (!rpcError) {
            console.log("Approach 2 succeeded: Profile created via RPC function");
            profileCreated = true;
          } else {
            console.error("Approach 2 failed - RPC function error:", {
              message: rpcError.message,
              details: rpcError.details,
              hint: rpcError.hint,
              code: rpcError.code
            });
            
            let errorMessage = rpcError.message;
            if (rpcError.message.includes('Unauthorized')) {
              errorMessage = `RPC authorization failed: ${rpcError.message}. The user may have been created too long ago or the security check failed.`;
            } else if (rpcError.message.includes('does not exist') || rpcError.code === '42883') {
              errorMessage = `RPC function not found: The database migration may not have been applied. Please contact support.`;
            } else if (rpcError.message.includes('User does not exist')) {
              errorMessage = `RPC error: User was not found in auth.users. This may indicate a timing issue.`;
            }
            
            profileCreationErrors.push(`RPC function: ${errorMessage}`);
          }
        }

        // If all approaches failed
        if (!profileCreated) {
          const errorDetails = profileCreationErrors.length > 0 
            ? `\n\nAttempted methods:\n${profileCreationErrors.map((err, idx) => `${idx + 1}. ${err}`).join('\n')}`
            : '';
          
          throw new Error(
            `Failed to create user profile. Please ensure email confirmation is disabled or contact support.${errorDetails}`
          );
        }

        // Mark invitation as used
        if (invitationId) {
          const { error: updateError } = await supabase
            .from("saas_invitations")
            .update({
              used_at: new Date().toISOString(),
              used_by: authData.user.id,
              used_email: email,
            })
            .eq("id", invitationId);

          if (updateError) {
            console.warn("Failed to mark invitation as used:", updateError);
            // Don't throw error here as the user is already created
          }
        }

        // Move to next step (company details)
        handleNext();
        return;
      }

      // If no invitation token, use OTP flow (should not happen if invitation is required)
      // Debug: Check Supabase connection
      console.log("Supabase URL:", import.meta.env.VITE_SUPABASE_URL);
      console.log("Supabase Anon Key:", import.meta.env.VITE_SUPABASE_ANON_KEY ? "Present" : "Missing");
      
      // Test basic Supabase connection
      try {
        const { data: testData, error: testError } = await supabase.from('profiles').select('count').limit(1);
        console.log("Supabase connection test:", { testData, testError });
      } catch (err) {
        console.error("Supabase connection failed:", err);
      }

      // Send OTP for email verification (this will create user if needed)
      const { data: otpData, error: otpError } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: true,
        },
      });

      console.log("OTP sent result:", { otpData, otpError });

      if (otpError) {
        console.error("OTP error details:", otpError);
        if (otpError.message.includes("already registered") || otpError.message.includes("already been registered")) {
          setIsExistingUser(true);
          setError("An account with this email already exists.");
          return;
        }
        if (otpError.message.includes("For security purposes")) {
          setError("Please wait a moment before requesting another verification code.");
          return;
        }
        throw otpError;
      }

      // Store user data for after verification
      setPendingUser({
        email,
        password,
        firstName,
        lastName,
      });

      // Show OTP verification
      setShowOTPVerification(true);
      setError("");
    } catch (err: any) {
      console.error("Signup error:", err);
      setError(err.message || "An error occurred during signup");
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyCreation = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Validate website URL
      if (
        companyDetails.website &&
        !companyDetails.website.match(
          /^https?:\/\/[A-Za-z0-9.-]+\.[A-Za-z]{2,}.*$/
        )
      ) {
        setError(
          "Please enter a valid website URL starting with http:// or https://"
        );
        return;
      }

      // Get user ID - try from stored ID first, then from session
      let userId: string | null = null;

      if (createdUserId) {
        // Use the user ID from step 1
        userId = createdUserId;
      } else {
        // Try to get from session
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) {
          userId = user.id;
        }
      }

      // If still no user ID, try to sign in with password (for invitation flow)
      if (!userId && invitationToken && password) {
        console.log("No session found, attempting to sign in with password");
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (signInError) {
          throw new Error(`Unable to authenticate. Please try refreshing the page or contact support. Error: ${signInError.message}`);
        }

        if (signInData?.user) {
          userId = signInData.user.id;
          setCreatedUserId(userId);
        }
      }

      if (!userId) {
        throw new Error("No authenticated user found. Please ensure you completed step 1 successfully.");
      }

      // Check if we have a session
      const { data: { session } } = await supabase.auth.getSession();

      // Create SaaS details - use RPC function if no session (private/incognito mode)
      if (!session) {
        console.log("No session found, using RPC function to create saas_details");
        const { error: rpcError } = await supabase.rpc('upsert_saas_details_for_invitation', {
          user_id: userId,
          company_name: companyDetails.company_name,
          website: companyDetails.website,
          contact_email: email,
          contact_phone: companyDetails.contact_phone,
          address: companyDetails.address || null,
          postal_code: companyDetails.postal_code || null,
          city: companyDetails.city || null,
          country: companyDetails.country || null,
        });

        if (rpcError) {
          console.error("RPC function error:", rpcError);
          throw new Error(`Failed to create company details: ${rpcError.message}`);
        }
      } else {
        // Use direct insert if we have a session
        const { error: saasError } = await supabase.from("saas_details").insert([
          {
            id: userId,
            company_name: companyDetails.company_name,
            website: companyDetails.website,
            contact_email: email,
            contact_phone: companyDetails.contact_phone,
            address: companyDetails.address,
            postal_code: companyDetails.postal_code,
            city: companyDetails.city,
            country: companyDetails.country,
          },
        ]);

        if (saasError) {
          console.error("Direct insert error:", saasError);
          // Fallback to RPC function if direct insert fails
          console.log("Direct insert failed, trying RPC function as fallback");
          const { error: rpcError } = await supabase.rpc('upsert_saas_details_for_invitation', {
            user_id: userId,
            company_name: companyDetails.company_name,
            website: companyDetails.website,
            contact_email: email,
            contact_phone: companyDetails.contact_phone,
            address: companyDetails.address || null,
            postal_code: companyDetails.postal_code || null,
            city: companyDetails.city || null,
            country: companyDetails.country || null,
          });

          if (rpcError) {
            throw new Error(`Failed to create company details: ${rpcError.message}`);
          }
        }
      }

      // Move to next step
      handleNext();
    } catch (err: any) {
      console.error("Company creation error:", err);
      setError(err.message || "An error occurred while saving company details");
    } finally {
      setLoading(false);
    }
  };

  const handleScopeSelection = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      // Get user ID - try from stored ID first, then from session
      let userId: string | null = null;

      if (createdUserId) {
        // Use the user ID from step 1
        userId = createdUserId;
      } else {
        // Try to get from session
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) {
          userId = user.id;
        }
      }

      // If still no user ID, try to sign in with password (for invitation flow)
      if (!userId && invitationToken && password) {
        console.log("No session found, attempting to sign in with password");
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (signInError) {
          throw new Error(`Unable to authenticate. Please try refreshing the page or contact support. Error: ${signInError.message}`);
        }

        if (signInData?.user) {
          userId = signInData.user.id;
          setCreatedUserId(userId);
        }
      }

      if (!userId) {
        throw new Error("No authenticated user found. Please ensure you completed the previous steps successfully.");
      }

      // Update saas_details with selected scope codes
      // Check if we have a session first
      const { data: { session: checkSession } } = await supabase.auth.getSession();
      
      let updateSuccess = false;
      
      if (checkSession) {
        // Try direct update if we have a session
        const { error: updateError } = await supabase
          .from("saas_details")
          .update({
            scopes: selectedScopes,
          })
          .eq("id", userId);

        if (!updateError) {
          updateSuccess = true;
        } else {
          console.error("Direct update failed:", updateError);
          // Will try RPC function as fallback
        }
      }

      // If direct update failed or no session, we need to ensure user is signed in first
      if (!updateSuccess) {
        if (!password) {
          throw new Error("Unable to save scopes. Please ensure you completed step 1 with a password.");
        }

        // Sign in with password to establish session
        console.log("Signing in to establish session for scope update");
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (signInError) {
          // Check if error is due to email not confirmed
          if (signInError.message.includes("Email not confirmed") || signInError.message.includes("email_not_confirmed")) {
            // For invitation flow, we can still save scopes using RPC function
            // The user will need to confirm email later, but we can save their preferences now
            console.log("Email not confirmed, using RPC function to save scopes without session");
            
            // Use RPC function to update scopes without requiring session
            const { error: rpcError } = await supabase.rpc('update_saas_scopes_for_invitation', {
              user_id: userId,
              scopes_array: selectedScopes,
            });

            if (rpcError) {
              throw new Error(`Failed to save scopes: ${rpcError.message}. Your account was created successfully, but email confirmation is required. Please check your email and confirm your account, then log in to complete the setup.`);
            }

            // Scopes saved successfully via RPC
            updateSuccess = true;
            
            // Show message about email confirmation
            setError("Your account was created successfully and your scope selections have been saved. However, email confirmation is required before you can log in. Please check your email and click the confirmation link, then log in to access your account.");
            
            // Wait a moment to show the message, then redirect to login
            setTimeout(() => {
              navigate("/login");
            }, 5000);
            return;
          }
          
          throw new Error(`Failed to authenticate: ${signInError.message}. Please try logging in manually.`);
        }

        if (!signInData?.session) {
          throw new Error("Session not established after sign-in. Please try logging in manually.");
        }

        // Wait for session to be established
        await new Promise(resolve => setTimeout(resolve, 500));

        // Try update again with session
        const { error: updateError2 } = await supabase
          .from("saas_details")
          .update({
            scopes: selectedScopes,
          })
          .eq("id", userId);

        if (updateError2) {
          throw new Error(`Failed to save scopes: ${updateError2.message}`);
        }

        updateSuccess = true;
      }

      if (!updateSuccess) {
        throw new Error("Failed to save selected scopes. Please try again.");
      }

      // Track SaaS onboarding completion
      analytics.trackSaaSOnboardingCompleted(userId);

      // Verify session is available before navigating
      const { data: { session: finalSession } } = await supabase.auth.getSession();
      
      if (!finalSession) {
        // One more attempt to sign in (only if we haven't already tried due to email confirmation)
        if (password) {
          console.log("Final session check failed, attempting one more sign-in");
          const { data: finalSignIn, error: finalError } = await supabase.auth.signInWithPassword({
            email,
            password,
          });

          if (finalError) {
            // Check if it's email confirmation issue
            if (finalError.message.includes("Email not confirmed") || finalError.message.includes("email_not_confirmed")) {
              // Scopes were already saved via RPC function above, so just show message and redirect
              setError("Your account was created successfully and your scope selections have been saved. However, email confirmation is required. Please check your email and click the confirmation link, then log in to access your account.");
              setLoading(false);
              setTimeout(() => {
                navigate("/login");
              }, 5000);
              return;
            }
            
            setError("Scopes saved successfully, but automatic login failed. Please login with your email and password.");
            setLoading(false);
            // Wait 3 seconds to show the error message before redirecting
            setTimeout(() => {
              navigate("/login");
            }, 3000);
            return;
          }

          if (!finalSignIn?.session) {
            setError("Scopes saved successfully, but automatic login failed. Please login with your email and password.");
            setLoading(false);
            setTimeout(() => {
              navigate("/login");
            }, 3000);
            return;
          }
          
          // Wait for session
          await new Promise(resolve => setTimeout(resolve, 500));
        } else {
          setError("Scopes saved successfully. Please login to access your account.");
          setLoading(false);
          setTimeout(() => {
            navigate("/login");
          }, 3000);
          return;
        }
      }

      // Navigate to SaaS dashboard only if everything succeeded and we have a session
      // Double-check we have a session before navigating
      const { data: { session: finalCheck } } = await supabase.auth.getSession();
      if (!finalCheck) {
        setError("Scopes saved successfully, but session not available. Please login to access your account.");
        setLoading(false);
        setTimeout(() => {
          navigate("/login");
        }, 3000);
        return;
      }
      
      navigate(ROUTES.extranet("saas"));
    } catch (err: any) {
      console.error("Scope selection error:", err);
      const errorMessage = err.message || "An error occurred while saving selected scopes";
      setError(errorMessage);
      setLoading(false);
      // IMPORTANT: Don't navigate on error - let user see the error and try again
      // The error will be displayed in the UI and user can retry
      return; // Explicitly return to prevent any further execution
    }
  };

  const handleOTPSuccess = async () => {
    if (!pendingUser) return;

    try {
      setLoading(true);
      setError("");

      // Get the current user (should be authenticated after OTP verification)
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        throw new Error("Failed to get authenticated user");
      }

      // Set password for the user so they can login normally in the future
      if (pendingUser.password) {
        const { error: passwordError } = await supabase.auth.updateUser({
          password: pendingUser.password
        });
        
        if (passwordError) {
          console.warn("Failed to set password:", passwordError);
          // Don't throw error here as the user is already authenticated
        }
      }

      // Create profile
      const profileData = {
        id: user.id,
        email: pendingUser.email,
        user_type: "saas",
        full_name: `${pendingUser.firstName} ${pendingUser.lastName}`,
      };
      
      const { error: profileError } = await supabase.from("profiles").insert([profileData]);

      if (profileError) throw profileError;

      // Clear pending user data and hide OTP verification
      setPendingUser(null);
      setShowOTPVerification(false);

      // Move to next step
      handleNext();
    } catch (err: any) {
      console.error("OTP success error:", err);
      setError(err.message || "An error occurred after verification");
    } finally {
      setLoading(false);
    }
  };

  const handleOTPError = (errorMessage: string) => {
    setError(errorMessage);
  };

  const handleOTPResend = () => {
    setError("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    switch (currentStep) {
      case 0:
        return handleUserCreation(e);
      case 1:
        return handleCompanyCreation(e);
      case 2:
        return handleScopeSelection(e);
      default:
        return;
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Left side - Form */}
      <div className="w-full md:w-1/2 bg-white p-4 md:p-8 flex flex-col relative">
        <div className="flex-1">
          {/* Progress indicator */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div
                  key={index}
                  className={`flex items-center ${
                    index < steps.length - 1 ? "flex-1" : ""
                  }`}
                >
                  <div
                    className={`
                      w-8 h-8 rounded-full flex items-center justify-center
                      ${
                        index <= currentStep
                          ? "bg-indigo-600 text-white"
                          : "bg-gray-200 text-gray-500"
                      }
                    `}
                  >
                    {index + 1}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`
                        flex-1 h-0.5 mx-4
                        ${index < currentStep ? "bg-indigo-600" : "bg-gray-200"}
                      `}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step content */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {steps[currentStep].title}
            </h2>
            <p className="text-gray-500">{steps[currentStep].description}</p>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{error}</p>
              {isExistingUser && (
                <p className="mt-2 text-sm text-gray-600">
                  Already have an account?{" "}
                  <Link
                    to="/login"
                    className="text-indigo-600 hover:text-indigo-500 font-medium"
                  >
                    Sign in here
                  </Link>
                </p>
              )}
              {invitationValid === false && !isExistingUser && (
                <p className="mt-2 text-sm text-gray-600">
                  If you believe this is an error, please{" "}
                  <Link
                    to="/"
                    className="text-indigo-600 hover:text-indigo-500 font-medium"
                  >
                    contact us
                  </Link>
                  {" "}for assistance.
                </p>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {currentStep === 0 && !showOTPVerification && invitationValid !== false && (
              <AccountForm
                email={email}
                setEmail={setEmail}
                password={password}
                setPassword={setPassword}
                firstName={firstName}
                setFirstName={setFirstName}
                lastName={lastName}
                setLastName={setLastName}
              />
            )}

            {currentStep === 0 && showOTPVerification && (
              <OTPVerification
                email={email}
                onSuccess={handleOTPSuccess}
                onError={handleOTPError}
                onResend={handleOTPResend}
                loading={loading}
              />
            )}

            {currentStep === 1 && (
              <CompanyForm
                companyDetails={companyDetails}
                setCompanyDetails={setCompanyDetails}
              />
            )}

            {currentStep === 2 && (
              <>
                <ScopeForm
                  scopes={scopes}
                  selectedScopes={selectedScopes}
                  toggleScope={(scopeCode) => {
                    // If we're deselecting a scope
                    if (selectedScopes.includes(scopeCode)) {
                      // Remove the scope from selected scopes
                      setSelectedScopes((prev) =>
                        prev.filter((code) => code !== scopeCode)
                      );
                    }
                    // If we're selecting a scope, just add it
                    else {
                      setSelectedScopes((prev) => [...prev, scopeCode]);
                    }
                  }}
                  loading={loading}
                />
              </>
            )}

            {!showOTPVerification && invitationValid !== false && (
              <div className="mt-8 flex justify-between">
                <button
                  type="button"
                  onClick={handleBack}
                  className="btn-secondary"
                >
                  Back
                </button>
                <button type="submit" disabled={loading || invitationValid === null} className="btn-primary">
                  {loading && invitationValid === null ? "Validating..." : currentStep < steps.length - 1 ? "Next" : "Complete Setup"}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Right side - Background Image */}
      <div className="hidden md:block md:w-1/2 relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${steps[currentStep].image})` }}
        />
      </div>
    </div>
  );
}
